import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobFamily } from '~/shared/data-contract/job-family';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { updateFamiliesWithClustersCache } from './use-families-with-clusters';

export type UseDeleteJobFamiliesParams = {
    readonly jobFamilies: NonEmptyArray<JobFamily>;
};

export type UseDeleteJobFamiliesResult = UseMutationResult<unknown, Error, UseDeleteJobFamiliesParams>;

export const useDeleteJobFamilies = (): UseDeleteJobFamiliesResult => {
    const { deleteJobFamilies } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobFamilies }: UseDeleteJobFamiliesParams) => {
            const result = await deleteJobFamilies(jobFamilies);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobFamilies[0];
            const idsToDelete = new Set(params.jobFamilies.map((family) => family.id));

            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });

            updateFamiliesWithClustersCache(queryClient, architectureId, (old) =>
                old.filter((family) => !idsToDelete.has(family.id)),
            );
        },
        onSuccess: (_, { jobFamilies }) => {
            const { architectureId } = jobFamilies[0];

            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });
        },
        throwOnError: false,
    });
};
